/**
 * The sum in front of the portal.
 *
 * A visitor the guard is unsure about is asked for a piece of arithmetic
 * rather than for four characters off a picture. The browser is given a
 * number and told to find another one whose fingerprint, taken together with
 * the first, begins with a run of zero bits. There is no way to work that out
 * - only to try numbers until one fits - so it costs whoever asks a fraction
 * of a second and costs a machine making a thousand requests a thousand
 * fractions of a second.
 *
 * The visitor does nothing. They see a line saying the portal is checking,
 * and then they are where they were going. That is the whole reason this
 * exists alongside the picture: a picture stops a robot by costing a person
 * four characters, and this stops most of them by costing a person nothing.
 */

import { query } from "../core/dom";
import { text } from "../core/i18n";

/** How often the page says how far along it is. */
const REPORT_EVERY = 2000;

/** Return the hexadecimal of the SHA-256 of `value`. */
async function fingerprint(value: string): Promise<string> {
    const bytes = new TextEncoder().encode(value);
    const digest = await crypto.subtle.digest("SHA-256", bytes);

    return Array.from(new Uint8Array(digest))
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("");
}

/** Return whether `hex` begins with `zeroes` zero bits. */
function begins(hex: string, zeroes: number): boolean {
    const whole = Math.floor(zeroes / 4);
    if (hex.slice(0, whole) !== "0".repeat(whole)) {
        return false;
    }

    const left = zeroes % 4;
    if (left === 0) {
        return true;
    }

    return parseInt(hex[whole] ?? "f", 16) < 2 ** (4 - left);
}

/**
 * Return the number which makes `puzzle` come out with `zeroes` zero bits.
 *
 * Yields to the browser every so often so the page never stops answering -
 * a check which freezes the tab it is checking has failed at being
 * invisible.
 */
export async function solve(
    puzzle: string,
    zeroes: number,
    report?: (tried: number) => void,
): Promise<number> {
    for (let tried = 0; ; tried += 1) {
        if (begins(await fingerprint(`${puzzle}:${tried}`), zeroes)) {
            return tried;
        }

        if (tried % REPORT_EVERY === 0 && tried > 0) {
            report?.(tried);
            await new Promise((wake) => setTimeout(wake, 0));
        }
    }
}

/** Work out the sum on the page which carries one, and send the answer. */
export function initProofOfWork(): void {
    const form = query<HTMLFormElement>("[data-proof-of-work]");
    if (!form || !crypto?.subtle) {
        // Without the browser's own cryptography there is nothing to work
        // with, and the page falls back to the picture it also carries.
        return;
    }

    const puzzle = form.dataset["puzzle"] ?? "";
    const zeroes = Number(form.dataset["zeroes"] ?? "0");
    const answer = form.elements.namedItem("proof");
    const saying = query<HTMLElement>("[data-proof-status]", form);

    if (!(answer instanceof HTMLInputElement) || !puzzle || !zeroes) {
        return;
    }

    void (async () => {
        const found = await solve(puzzle, zeroes, () => {
            if (saying) {
                saying.textContent = text("js.proof.working", "Overujeme prehliadač...");
            }
        });

        answer.value = String(found);
        if (saying) {
            saying.textContent = text("js.proof.done", "Hotovo, pokračujeme.");
        }

        form.submit();
    })();
}
